import { useState } from 'react';
import { useFlags } from '@atlaskit/flag';
import SharedButton from 'shared/components/buttons/button';
import DevToolsApiService from 'core/api/dev-tools/dev-tools-api.service';
import { IDeliveryRequestClearCounts } from 'core/api/dev-tools/dev-tools-api-interface';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';

/**
 * Empties the Delivery Request screens, for testing and demos.
 *
 * Two presses, and the first only counts: the second button names what it is
 * about to delete, so nobody clears dev without seeing the numbers. Takes the
 * jobs those requests booked and the batches built from them too, so nothing
 * is left in a driver's pool pointing at a request that has gone — and the
 * jobs legacy-format test emails book directly, which have no request at all.
 */

const plural = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;

const describe = (counts: IDeliveryRequestClearCounts) =>
  `${plural(counts.requests, 'request')}, ${plural(counts.jobs, 'job')} and ${plural(counts.batches, 'batch', 'batches')}`;

const ClearRequestsPanel = () => {
  const flags = useFlags();
  const [counts, setCounts] = useState<IDeliveryRequestClearCounts>();
  const [busy, setBusy] = useState(false);

  const count = async () => {
    setBusy(true);
    try {
      const response = await DevToolsApiService.clearDeliveryRequests(false);
      setCounts(response.counts);
    } catch (error) {
      showErrorFlag('Could not count requests', (error as Error).message, flags);
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      const response = await DevToolsApiService.clearDeliveryRequests(true);
      showSuccessFlag('Cleared', `Deleted ${describe(response.counts)}.`, flags);
      setCounts(undefined);
    } catch (error) {
      showErrorFlag('Clear failed', (error as Error).message, flags);
    } finally {
      setBusy(false);
    }
  };

  const nothing = counts && !counts.requests && !counts.jobs && !counts.batches;

  return (
    <div className='rounded border border-red-200 p-5' data-testid='clear-requests-panel'>
      <p className='body-02 font-semibold'>Clear out requests</p>
      <p className='body-01 mt-1 text-gray-600'>
        Deletes every delivery request, the transport jobs they booked, the jobs made directly by legacy-format test
        emails, and the batches built from those jobs. Request types, routing settings and jobs created any other way
        are kept.
      </p>

      <div className='mt-4 flex items-center gap-3'>
        {!counts && (
          <SharedButton
            type='button'
            appearance='default'
            label='Count what would go'
            isLoading={busy}
            onClick={count}
            testId='count-requests'
          />
        )}

        {counts && nothing && <p className='body-01 text-gray-600'>Nothing to clear.</p>}

        {counts && !nothing && (
          <>
            <SharedButton
              type='button'
              appearance='danger'
              label={`Delete ${describe(counts)}`}
              isLoading={busy}
              onClick={clear}
              testId='confirm-clear-requests'
            />
            <SharedButton
              type='button'
              appearance='subtle'
              label='Cancel'
              disabled={busy}
              onClick={() => setCounts(undefined)}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ClearRequestsPanel;
